import { Alignment, DOMUtil } from "@mcleod/core";
import { AnchoredComponentPositioner, AnchoredComponentType } from "../AnchoredComponentPositioner";
import { AnchorMutation } from "../../Anchor";
import { OnScreen } from "./OnScreen";

/**
 * Uses the our `OnScreen` utility class to position anchored components (tooltips or overlays).
 */
export class OnScreenPositioner extends AnchoredComponentPositioner {
    private static spacing = 8;
    private lastSize: DOMRect;

    override async listenForPositionUpdates() {
        await this.computeAndUpdatePosition();
        this.lastSize = this.currentMutation.newRect;
        this.anchoredComponent.addResizeListener(() => {
            const thisSize = this.anchoredComponent._element.getBoundingClientRect();
            // this condition prevents the anchored comp from shrinking but that seems valid to want to do in some cases.  So far, we only need to grow tooltips, though, so this is a workaround.
            // ths condition was added because a tooltip was shrinking horizontally.  Even though this resize handler doesn't specifically change the
            // size, it does change the position.  Somehow (I'm sure it's explainable), setting the left of an element near the edge of the page will change the size.
            // This shrink caused it to grow vertically, which triggered a resize again.
            // The tooltip with the problem the "Save" button in the upper-right of CrudDecorator when it had a disabled reason (You can't save because you haven't changed anything)
            // **** If this button was not on the edge of the page, this effect didn't happen.
            if (this.lastSize == null || thisSize.height > this.lastSize.height || thisSize.width > this.lastSize.width) {
                this.computeAndUpdatePosition();
            }
            this.lastSize = thisSize;
        });
    }

    override getDefaultPosition(): Alignment {
        if (this.componentType === AnchoredComponentType.TOOLTIP) {
            const anchorBounds = this.anchorElement.getBoundingClientRect();
            const anchorCenterX = anchorBounds.left + (anchorBounds.width / 2);
            const anchorCenterY = anchorBounds.top + (anchorBounds.height / 2);
            if (anchorCenterY > window.innerHeight * .8) {
                return anchorCenterX < window.innerWidth * .8 ? Alignment.RIGHT : Alignment.LEFT;
            }
        }
        return super.getDefaultPosition();
    }

    computePosition(): Promise<AnchorMutation> {
        const onScreen = this.componentType === AnchoredComponentType.TOOLTIP ? this.computeTooltipPosition() : this.computeOverlayPosition();
        return Promise.resolve(onScreen);
    }

    computeOverlayPosition(): AnchorMutation {
        const minWidth = this.anchorProps?.minWidth || DOMUtil.convertSizeStyleToPixels(this.anchoredComponent.minWidth, document.body.offsetWidth);
        const minHeight = this.anchorProps?.minHeight || DOMUtil.convertSizeStyleToPixels(this.anchoredComponent.minHeight, document.body.offsetHeight);
        return OnScreen.ensureOnScreen(this.anchoredComponent, { ...this.anchorProps, minWidth: minWidth, minHeight: minHeight });
    }

    computeTooltipPosition(): AnchorMutation {
        const anchorRect = this.anchorElement.getBoundingClientRect();
        const compRect = this.anchoredComponent._element.getBoundingClientRect();
        const onScreen = OnScreen.ensureRectOnScreen(compRect, { ...this.anchorProps});
        const positionResult = this.computePositionRect(anchorRect, onScreen);
        return {...onScreen, newRect: positionResult}
    }

    private computePositionRect(anchorRect: DOMRect, mutation: AnchorMutation): DOMRect {
        const compRect = mutation.newRect;
        const newRect = DOMRect.fromRect(mutation.newRect);
        const newAnchorPosition = mutation.newAnchor.position
        if (newAnchorPosition === Alignment.TOP) {
            newRect.y = anchorRect.top - compRect.height - OnScreenPositioner.spacing;
            newRect.x = (anchorRect.left + (anchorRect.width / 2)) - (compRect.width / 2);
        } else if (newAnchorPosition === Alignment.RIGHT) {
            newRect.y = (anchorRect.top + (anchorRect.height / 2)) - (compRect.height / 2);
            newRect.x = anchorRect.left + anchorRect.width + OnScreenPositioner.spacing;
        } else if (newAnchorPosition === Alignment.LEFT) {
            newRect.y = (anchorRect.top + (anchorRect.height / 2)) - (compRect.height / 2);
            newRect.x = anchorRect.left - compRect.width - OnScreenPositioner.spacing;
        } else {
            newRect.y = anchorRect.bottom + OnScreenPositioner.spacing;
            newRect.x = (anchorRect.left + (anchorRect.width / 2)) - (compRect.width / 2);
        }
        if (newRect.x < 4)
            newRect.x = 4;
        if (newRect.y < 4)
            newRect.y = 4;

        return newRect;
    }

    override getAdjustedPointerStyle(): Partial<CSSStyleDeclaration> {
        const result = super.getAdjustedPointerStyle();
        const pointerBounds = this.pointer?._element.getBoundingClientRect();
        const anchorBounds = this.anchorElement.getBoundingClientRect();
        const compBounds = this.anchoredComponent._element.getBoundingClientRect();
        const position = this.currentMutation.newAnchor.position;
        const pointerSize = this.pointerSize;

         if (position === Alignment.LEFT || position === Alignment.RIGHT) {
            // Vertical centering (for left/right positions)
            const isLeft = position === Alignment.LEFT;
            const anchorMiddle = anchorBounds.top + anchorBounds.height / 2;
            const pointerMiddle = pointerBounds.top + pointerBounds.height / 2;
            const difference = anchorMiddle - pointerMiddle;
            let offsetY = 0;
            if (pointerMiddle - difference > compBounds.top) {
                offsetY = difference;
            }
            const pointerOffsetY = pointerSize / -2 + offsetY;

            return {
                ...result,
                top: "50%",
                marginTop: `${pointerOffsetY}px`,
                left: isLeft ? "100%" : "",
                marginLeft: isLeft ? "0px" : `-${pointerSize}px`,
            };
        } else {
            // Horizontal centering (for top/bottom positions)
            const isTop = position === Alignment.TOP;
            const anchorMiddle = anchorBounds.left + anchorBounds.width / 2;
            const pointerMiddle = pointerBounds.left + pointerBounds.width / 2;
            const difference = anchorMiddle - pointerMiddle;
            let offsetX = 0;
            if (pointerMiddle - difference > compBounds.left) {
                offsetX = difference;
            }
            const pointerOffsetX = pointerSize / -2 + offsetX;
            return {
                ...result,
                left: "50%",
                marginLeft: `${pointerOffsetX}px`,
                top: isTop ? "100%" : "",
                marginTop: isTop ? "0px" : `-${pointerSize}px`
            };
        }
    }

}
